import { useCallback, useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../../firebase';
import {
    BuildingExportResult,
    ExportProgress,
    exportBuildingToZip,
} from '../../api/buildingTransferApi';

export interface BuildingOption {
    key: string;
    name: string;
}

interface UseBuildingExportReturn {
    loadingBuildings: boolean;
    buildings: BuildingOption[];
    selectedKey: string;
    setSelectedKey: (key: string) => void;
    exporting: boolean;
    progress: ExportProgress | null;
    result: BuildingExportResult | null;
    error: string | null;
    exportSelected: () => Promise<void>;
}

export const useBuildingExport = (): UseBuildingExportReturn => {
    const [loadingBuildings, setLoadingBuildings] = useState(true);
    const [buildings, setBuildings] = useState<BuildingOption[]>([]);
    const [selectedKey, setSelectedKey] = useState('');
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress] = useState<ExportProgress | null>(null);
    const [result, setResult] = useState<BuildingExportResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const snapshot = await get(ref(db, 'buildingsSummary'));
                if (cancelled) return;
                const options: BuildingOption[] = [];
                if (snapshot.exists()) {
                    for (const [key, value] of Object.entries(snapshot.val() as Record<string, unknown>)) {
                        const name =
                            value && typeof value === 'object' && typeof (value as { name?: unknown }).name === 'string'
                                ? (value as { name: string }).name
                                : key;
                        options.push({ key, name });
                    }
                }
                options.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
                setBuildings(options);
            } catch (err) {
                console.error('Error loading building list:', err);
                if (!cancelled) setError('Byggnadslistan kunde inte läsas');
            } finally {
                if (!cancelled) setLoadingBuildings(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const exportSelected = useCallback(async () => {
        const building = buildings.find(option => option.key === selectedKey);
        if (!building || exporting) return;
        setExporting(true);
        setError(null);
        setResult(null);
        setProgress(null);
        try {
            const exportResult = await exportBuildingToZip(building.key, building.name, setProgress);
            setResult(exportResult);
        } catch (err) {
            // The user closing the save dialog is a cancel, not an error
            if (err instanceof DOMException && err.name === 'AbortError') {
                setProgress(null);
            } else {
                console.error('Error exporting building:', err);
                setError(err instanceof Error ? err.message : 'Exporten misslyckades');
            }
        } finally {
            setExporting(false);
        }
    }, [buildings, selectedKey, exporting]);

    return {
        loadingBuildings,
        buildings,
        selectedKey,
        setSelectedKey,
        exporting,
        progress,
        result,
        error,
        exportSelected,
    };
};
